import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useLocalGame } from '../lib/localGame';

const DIFFICULTIES = [
  { id: 'beginner', name: 'Beginner', blurb: 'Random legal moves. The board is yours.' },
  { id: 'easy', name: 'Easy', blurb: 'Light search. Beats casual mistakes.' },
  { id: 'medium', name: 'Medium', blurb: 'Solid tactics. Punishes loose pieces.' },
  { id: 'hard', name: 'Hard', blurb: 'Deep search. Plays sharp lines.' },
  { id: 'adaptive', name: 'Adaptive', blurb: 'Searches deeper as the board opens.' },
] as const;

const TIME_PRESETS = [
  { label: '3 + 0', initial: 180, increment: 0 },
  { label: '5 + 3', initial: 300, increment: 3 },
  { label: '10 + 0', initial: 600, increment: 0 },
  { label: '15 + 10', initial: 900, increment: 10 },
];

export function PlayBotRoute() {
  const [difficulty, setDifficulty] = useState<string>('beginner');
  const [time, setTime] = useState(TIME_PRESETS[1]!);
  const navigate = useNavigate();
  const startBot = useLocalGame((s) => s.startBot);

  return (
    <div className="h-full w-full max-w-[1100px] mx-auto px-5 md:px-10 py-6 grid grid-rows-[auto_1fr] gap-5">
      <div>
        <h1 className="font-display text-3xl font-bold">Play a bot</h1>
        <p className="text-sm text-ink-muted">
          Pick a difficulty and a clock. Cards are drafted at the start.
        </p>
      </div>
      <div className="min-h-0 grid lg:grid-cols-[1.4fr_1fr] gap-5">
        <Panel className="p-5 flex flex-col gap-5">
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">Difficulty</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDifficulty(d.id)}
                  className={`text-left rounded-card p-4 border transition focus-ring ${
                    difficulty === d.id
                      ? 'border-accent bg-accent/5 shadow-card'
                      : 'border-ink/10 hover:bg-ink/[0.03]'
                  }`}
                >
                  <div className="font-semibold">{d.name}</div>
                  <div className="text-xs text-ink-muted mt-0.5">{d.blurb}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-muted mt-3">
              Every bot move is checked against the same custom-rule validator used for players.
            </p>
          </section>
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">Time control</h2>
            <div className="flex flex-wrap gap-2">
              {TIME_PRESETS.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => setTime(t)}
                  className={`px-4 h-10 rounded-md font-mono text-sm border transition focus-ring ${
                    time.label === t.label
                      ? 'border-accent bg-accent/5'
                      : 'border-ink/10 hover:bg-ink/[0.03]'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </section>
        </Panel>
        <Panel className="p-5 flex flex-col">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">Start</h2>
          <div className="text-sm text-ink-muted mb-4">
            Difficulty: <span className="text-ink font-medium">{DIFFICULTIES.find((d) => d.id === difficulty)?.name}</span>
            <br />
            Time control: <span className="text-ink font-medium font-mono">{time?.label}</span>
          </div>
          <Button
            size="lg"
            className="mt-auto"
            onClick={() => {
              if (!time) return;
              startBot({ initialSeconds: time.initial, incrementSeconds: time.increment, difficulty });
              navigate({ to: '/game' });
            }}
          >
            Start game
          </Button>
        </Panel>
      </div>
    </div>
  );
}
